// The campus shuttle: one loop, a handful of stops, and a bus whose position
// arrives from two unreliable places — a phone left plugged in on the bus, and
// students tapping "it's here". Everything that has to AGREE between the server
// (which turns pings into leg timings) and the phones (which turn a position
// into "in 4 min") lives here, so the two can't drift into disagreeing about
// where the bus is on its loop.
//
// The route file is authored in UMapsTools (Commuting → Shuttle) and synced to
// `public/Map Info/pathfinder/shuttle_route.json`:
//
//   { version: 1, loop: true,
//     stops: [{ id, name, lat, lng, navNodeId? }],   // in driving order
//     path:  [{ lat, lng }],                          // the ROAD, a closed ring
//     schedule: { start: "07:30", startStop?: "sd-lot-1", everyMin: 18,   // optional
//                 dwellSec: 20, end?: "17:30", tz?: "America/Winnipeg",
//                 days?: [1, 2, 3, 4, 5] },                 // ISO weekdays
//     pace: { dwell: { [stopId]: sec },                         // optional,
//             legs:  { ["a>b"]: [sec, …] } } }                  // MEASURED
//
// `schedule` is the TIMETABLE, and it is a model rather than a list of times:
// a cycle starts with the bus AT stop 0 at `start` and every `everyMin` after,
// it waits `dwellSec` at every stop, and it drives the rest of the cycle at one
// constant speed. Every stop's scheduled time falls out of that — which is
// what lets the app say "3 min late" about a bus seen anywhere on the road,
// not only at a stop. `end` (optional) is when the SERVICE ends: no time is
// promised after it, and before the bus is at `startStop` at `start` nothing
// is promised either — outside those hours every ETA function here answers
// with nothing (`inService`), on the server and on every phone alike. `days`
// (optional) is which DAYS it runs, as ISO weekdays in `tz` (1 = Monday …
// 7 = Sunday); ABSENT means Monday to Friday — the shuttle does not run at
// weekends, and every route file authored before the field says nothing.
//
// `pace` is what the tracker has actually SEEN the bus do, written by
// UMapsWeb `scripts/shuttle-pace.mjs` from its recorded drives: how long it
// stands at each stop, and how long each stretch of each leg takes to drive.
// It is what a bus nobody has reported for a while is run forward on
// (`simulatedAt`) — see "The pace" below for why the timetable cannot be.
//
// `path` is what the bus drives; `stops` are projected onto it. A route with no
// path (or one vertex) falls back to straight lines between the stops, which is
// wrong across a building and right enough to test with.
//
// Position along the loop is a single number — metres from stop 0 along the
// path, in [0, length). Every question here (which leg is it on, has it passed
// a stop, how far to the next one) is arithmetic on that number, which is what
// keeps a bus from being "between Dysart and Ellis" on one client and "at
// Dafoe" on the other.
//
// No clock is read inside this file: `now` is always passed in.

import { haversineMeters } from './geo.js';

// ── Tunables. Shared, because the server and the phone must agree on them. ──

/** A tracker ping older than this is not a position, it is a memory. */
export const TRACKER_FRESH_MS = 3 * 60 * 1000;
/** A crowd report older than this says nothing about where the bus is now. */
export const CROWD_TTL_MS = 5 * 60 * 1000;
/** A gap between tracker pings longer than this breaks the leg being timed. */
export const MAX_PING_GAP_MS = 5 * 60 * 1000;
/** Metres off the road a position may be and still be snapped onto it. */
export const MAX_OFF_ROUTE_M = 120;
/** A crowd report must be this close to the road — you're at a stop or on the bus. */
export const CROWD_MAX_OFF_M = 60;
/** Two reports this close along the loop are about the same bus pass. */
export const CROWD_CLUSTER_M = 250;
/** Backwards movement smaller than this is GPS jitter, not reversing. */
export const JITTER_BACK_M = 40;
/** A leg longer than this is a lunch break or a detour, not a leg. */
export const LEG_MAX_SEC = 20 * 60;
/** Until a leg has this many timed samples, its ETA comes from distance. */
export const MIN_LEG_SAMPLES = 3;
/** Distance-based fallback: average speed including traffic, and time at a stop. */
export const DEFAULT_SPEED_MPS = 5.5; // ~20 km/h
export const DEFAULT_DWELL_SEC = 30;

/** The admin's choice of where the public position comes from. */
/**
 * 'auto' prefers a fresh tracker and falls back to the crowd; 'tracker' and
 * 'crowd' listen to one source only; 'simulate' ignores both and replays a
 * recorded loop of the real bus, forever. The last one is a DEMO: it puts a
 * bus on every student's map that is not on the road, so the admin page says
 * so in as many words. The replay itself is the server's — see
 * `api/_shuttle.js` — because the recording lives there, not here.
 */
export const SHUTTLE_MODES = ['auto', 'tracker', 'crowd', 'simulate'];

// ── Local planar metres. Campus is 2 km across; equirectangular is exact enough.

function planar(origin) {
  const kx = 111320 * Math.cos((origin.lat * Math.PI) / 180);
  const ky = 110540;
  return {
    to: p => ({ x: (p.lng - origin.lng) * kx, y: (p.lat - origin.lat) * ky }),
    from: q => ({ lat: origin.lat + q.y / ky, lng: origin.lng + q.x / kx }),
  };
}

const finite = v => typeof v === 'number' && Number.isFinite(v);
const validPt = p => p && finite(p.lat) && finite(p.lng)
  && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180;

/**
 * Validate an authored route file. Returns `{ stops, path, loop }` with bad
 * rows dropped, or null when fewer than two stops survive — a "loop" of one
 * stop has no legs and nothing to estimate.
 */
export function normalizeRoute(doc) {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.stops)) return null;
  const seen = new Set();
  const stops = [];
  for (const s of doc.stops) {
    if (!s || !validPt(s)) continue;
    const id = String(s.id ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const out = { id, name: String(s.name ?? id).trim() || id, lat: s.lat, lng: s.lng };
    if (typeof s.navNodeId === 'string' && s.navNodeId) out.navNodeId = s.navNodeId;
    stops.push(out);
  }
  if (stops.length < 2) return null;
  const path = Array.isArray(doc.path)
    ? doc.path.filter(validPt).map(p => ({ lat: p.lat, lng: p.lng }))
    : [];
  return { stops, path: path.length >= 2 ? path : [], loop: doc.loop !== false };
}

/**
 * Build the model every other function takes: the path as a closed ring
 * starting AT stop 0, with cumulative distances, and each stop's position
 * along it. Returns null for a route `normalizeRoute` refuses.
 *
 *   { stops: [{ ...stop, at }], ring: [{ lat, lng, x, y, at }], length, proj }
 */
export function buildRoute(doc) {
  const norm = normalizeRoute(doc);
  if (!norm) return null;
  const raw = norm.path.length >= 2 ? norm.path : norm.stops;
  const origin = raw[0];
  const proj = planar(origin);

  // Close the ring, and drop a repeated closing vertex the author clicked back onto.
  let pts = raw.map(p => ({ lat: p.lat, lng: p.lng, ...proj.to(p) }));
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (pts.length > 2 && Math.hypot(first.x - last.x, first.y - last.y) < 1) pts = pts.slice(0, -1);
  const ring0 = measure([...pts, { ...pts[0] }]);
  const len0 = ring0[ring0.length - 1].at;
  if (!(len0 > 0)) return null;

  // Rotate the ring so position 0 is stop 0. Then "metres from the start of
  // the loop" and "metres since Dysart" are the same number, and a trip is
  // literally one wrap of it.
  const s0 = projectRing(ring0, len0, proj.to(norm.stops[0]));
  const ring = measure(rotate(ring0, s0));
  const length = ring[ring.length - 1].at;

  // Each subsequent stop is placed FORWARD of the previous one. The loop can
  // pass close to itself (the U Lot detour runs down and back up a block
  // apart), so the globally nearest segment is not always the right one —
  // among near-equal candidates, the first one reached driving forward wins.
  const stops = [];
  let prev = 0;
  norm.stops.forEach((s, i) => {
    const at = i === 0 ? 0 : forwardProject(ring, length, proj.to(s), prev, 25).at;
    stops.push({ ...s, at });
    prev = at;
  });
  const timetable = timetableFor(stops, length, normalizeSchedule(doc.schedule));
  return { stops, ring, length, proj, loop: norm.loop, timetable, pace: normalizePace(doc.pace, stops) };
}

function measure(pts) {
  let at = 0;
  return pts.map((p, i) => {
    if (i > 0) at += Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y);
    return { ...p, at };
  });
}

// Rotate a closed ring (last vertex == first) so it starts at projection `s`.
function rotate(ring, s) {
  const n = ring.length - 1; // unique vertices
  const start = { lat: 0, lng: 0, x: s.x, y: s.y };
  const out = [start];
  for (let k = 1; k <= n; k++) out.push(ring[(s.seg + k) % n]);
  out.push({ ...start });
  // Drop zero-length hops the rotation created at a vertex.
  return out.filter((p, i) => i === 0 || Math.hypot(p.x - out[i - 1].x, p.y - out[i - 1].y) > 0.01
    || i === out.length - 1);
}

// Every segment's closest point to q.
function candidates(ring, q) {
  const out = [];
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i];
    const b = ring[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((q.x - a.x) * dx + (q.y - a.y) * dy) / l2)) : 0;
    const x = a.x + t * dx;
    const y = a.y + t * dy;
    out.push({ seg: i, x, y, at: a.at + t * (b.at - a.at), off: Math.hypot(q.x - x, q.y - y) });
  }
  return out;
}

function projectRing(ring, length, q) {
  let best = null;
  for (const c of candidates(ring, q)) if (!best || c.off < best.off) best = c;
  return { ...best, at: best.at % length };
}

/** Forward distance from a to b around a loop of `length`. */
export function forwardDistance(a, b, length) {
  return ((b - a) % length + length) % length;
}

// Among candidates within `slack` metres of the best perpendicular distance,
// take the one reached first driving forward from `from`.
function forwardProject(ring, length, q, from, slack) {
  const cs = candidates(ring, q);
  const bestOff = Math.min(...cs.map(c => c.off));
  let pick = null;
  let pickD = Infinity;
  for (const c of cs) {
    if (c.off > bestOff + slack) continue;
    const at = c.at % length;
    let d = forwardDistance(from, at, length);
    if (length - d < JITTER_BACK_M) d = 0; // a hair behind is "still here"
    if (d < pickD) { pick = { ...c, at }; pickD = d; }
  }
  return pick;
}

/**
 * Snap a point onto the road. With `hintAt` (the last known position) a
 * point near two stretches of road snaps to the one just ahead of where the
 * bus was, rather than to whichever happens to be a metre closer.
 *
 * Returns `{ at, lat, lng, off, seg }`, or null for no route / no point.
 */
export function projectOnRoute(route, pt, hintAt = null) {
  if (!route || !validPt(pt)) return null;
  const q = route.proj.to(pt);
  const c = finite(hintAt)
    ? forwardProject(route.ring, route.length, q, hintAt, 20)
    : projectRing(route.ring, route.length, q);
  return { ...route.proj.from(c), at: c.at, off: c.off, seg: c.seg };
}

/** The point, and the bearing of the road, at `at` metres along the loop. */
export function pointAt(route, at) {
  const { ring, length } = route;
  const a0 = ((at % length) + length) % length;
  let i = 0;
  while (i < ring.length - 2 && ring[i + 1].at < a0) i++;
  const a = ring[i];
  const b = ring[i + 1];
  const span = b.at - a.at;
  const t = span > 0 ? (a0 - a.at) / span : 0;
  const q = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) };
  const bearing = ((Math.atan2(b.x - a.x, b.y - a.y) * 180) / Math.PI + 360) % 360;
  return { ...route.proj.from(q), at: a0, bearing };
}

/**
 * The road between two positions, driving forward, as {lat,lng} points —
 * both ends included. What a marker animates along, so the bus turns the
 * corner rather than cutting across the building on it.
 */
export function pathBetween(route, fromAt, toAt) {
  const { ring, length } = route;
  const d = forwardDistance(fromAt, toAt, length);
  const out = [pointAt(route, fromAt)];
  const inner = [];
  for (let k = 0; k < 2; k++) { // at most one wrap
    for (const v of ring.slice(1, -1)) {
      const vAt = v.at + k * length;
      const rel = vAt - fromAt;
      if (rel > 0 && rel < d) inner.push({ rel, p: route.proj.from(v) });
    }
  }
  inner.sort((a, b) => a.rel - b.rel);
  for (const { p } of inner) out.push(p);
  out.push(pointAt(route, toAt));
  return out.map(p => ({ lat: p.lat, lng: p.lng }));
}

/** The drawn line: the whole loop, closed, as {lat,lng}. */
export function routeLine(route) {
  return route ? route.ring.map(v => route.proj.from(v)) : [];
}

/** Index i such that `at` is on the leg from stop i to stop i+1. */
export function legIndexAt(route, at) {
  const { stops } = route;
  let i = 0;
  while (i < stops.length - 1 && stops[i + 1].at <= at) i++;
  return i;
}

/** The stable key a leg's timings are filed under: "dysart>dafoe". */
export function legKey(route, i) {
  const n = route.stops.length;
  return `${route.stops[i % n].id}>${route.stops[(i + 1) % n].id}`;
}

/** Road metres of leg i. */
export function legLength(route, i) {
  const n = route.stops.length;
  return forwardDistance(route.stops[i % n].at, route.stops[(i + 1) % n].at, route.length) || route.length;
}

/** The closest stop to a point, as the crow flies — "Nearest shuttle stop". */
export function nearestStop(route, pt) {
  if (!route || !validPt(pt)) return null;
  let best = null;
  for (const s of route.stops) {
    const m = haversineMeters(pt, s);
    if (!best || m < best.meters) best = { stop: s, meters: m };
  }
  return best;
}

// ── The timetable ────────────────────────────────────────────────────────────

/** Metres from a stop within which the bus counts as AT it (waiting there). */
export const AT_STOP_M = 20;
/** The campus's zone, for a timetable that names none and a route with no timetable at all. */
export const SHUTTLE_TZ = 'America/Winnipeg';
/** Within this many seconds of the timetable the bus is "on time". */
export const ON_TIME_SEC = 60;

const HHMM = /^(\d{1,2}):(\d{2})$/;
function parseHHMM(v) {
  const m = HHMM.exec(String(v ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  return h <= 23 && mi <= 59 ? h * 60 + mi : null;
}

/**
 * The days a timetable runs when it does not say: Monday to Friday, as ISO
 * weekdays. ⚠️ A DEFAULT, not a fallback for broken data — the campus shuttle
 * does not run at weekends, and the live `shuttle_route.json` carries no
 * `days`, so this is what makes Saturday and Sunday promise nothing without a
 * data change (one would move the data stamp and cost every phone a refetch).
 */
export const DEFAULT_SERVICE_DAYS = Object.freeze([1, 2, 3, 4, 5]);

/**
 * `days` → sorted, de-duplicated ISO weekdays (1 = Monday … 7 = Sunday).
 * Absent, not an array, or with no valid day in it → `DEFAULT_SERVICE_DAYS`.
 * An empty or junk list is read as the default rather than as "never runs":
 * a service that never runs would silently blank every time on every phone,
 * which is a data-entry slip presenting as a dead feature.
 */
function normalizeDays(raw) {
  if (!Array.isArray(raw)) return [...DEFAULT_SERVICE_DAYS];
  const out = new Set();
  for (const d of raw) {
    const n = typeof d === 'number' ? d : typeof d === 'string' && d.trim() ? Number(d) : NaN;
    if (Number.isInteger(n) && n >= 1 && n <= 7) out.add(n);
  }
  return out.size ? [...out].sort((a, b) => a - b) : [...DEFAULT_SERVICE_DAYS];
}

/**
 * Validate a `schedule` block. Returns `{ startMin, endMin, everySec,
 * dwellSec, tz, startStop, days }` (minutes after local midnight) or null — and
 * null means NO timetable, which is a real state: times then come from
 * distance alone.
 *
 * `end` is the end of SERVICE — the last minute at which any stop time may be
 * shown — not the last cycle's start. The real service runs 07:30–17:30; a
 * cycle that begins at 17:12 is listed only as far as 17:30, and from 17:30
 * until the next morning's 07:30 nothing is listed at all. (It used to be the
 * last cycle's start, with times after it rolling over to tomorrow's first —
 * which showed a student at 18:00 a bus for 07:30 the next day as if it were
 * next, and showed times from 07:15 for a bus that leaves SD Lot at 07:30.)
 *
 * `startStop` names the stop the bus is AT at `start` — the real service
 * leaves SD Lot 1 at 07:30, and without it `start` could only ever describe
 * stop 0, which is a stop the morning's first bus reaches eight minutes in.
 * It is carried through raw here (a name, and this function has no stops to
 * resolve it against) and resolved by `timetableFor`; absent or unknown, the
 * anchor stays stop 0, which is every schedule authored before the field.
 */
export function normalizeSchedule(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const startMin = parseHHMM(raw.start);
  const everyMin = Number(raw.everyMin);
  const dwellSec = raw.dwellSec == null ? DEFAULT_DWELL_SEC : Number(raw.dwellSec);
  if (startMin == null || !(everyMin > 0 && everyMin <= 180) || !(dwellSec >= 0 && dwellSec <= 600)) return null;
  const endMin = raw.end == null || raw.end === '' ? null : parseHHMM(raw.end);
  if (raw.end != null && raw.end !== '' && (endMin == null || endMin < startMin)) return null;
  const tz = typeof raw.tz === 'string' && raw.tz.trim() ? raw.tz.trim() : SHUTTLE_TZ;
  const startStop = typeof raw.startStop === 'string' && raw.startStop.trim() ? raw.startStop.trim() : null;
  return { startMin, endMin, everySec: everyMin * 60, dwellSec, tz, startStop, days: normalizeDays(raw.days) };
}

/**
 * The timetable as offsets into one cycle. Stop i is reached at `arrive[i]`
 * seconds after the cycle starts and left `dwellSec` later; stop 0 is reached
 * at 0 and again at `cycleSec`, which is the next cycle's 0. Null when the
 * stops alone use up the whole cycle — no speed could keep that timetable.
 */
function timetableFor(stops, length, schedule) {
  if (!schedule) return null;
  const n = stops.length;
  const driveSec = schedule.everySec - n * schedule.dwellSec;
  if (!(driveSec > 0) || !(length > 0)) return null;
  const speed = length / driveSec;
  const arrive = stops.map((s, i) => i * schedule.dwellSec + s.at / speed);
  // `startStop` re-anchors the day: the authored `start` is when the bus is
  // AT that stop, so cycle 0 begins at stop 0 `arrive[idx]` seconds EARLIER.
  // `startMin` may come out fractional; everything downstream multiplies it
  // out. The cost is a phantom first pass of the stops before `startStop` —
  // the model claims the loop's opening minutes happened when the bus in
  // fact began mid-loop — which reads as "just missed it" at those stops for
  // under `arrive[idx]` seconds once a day, where anchoring at stop 0 told
  // every student at the real first stop a time one whole leg-walk late.
  // An id the stops do not carry anchors at stop 0, like every schedule
  // authored before the field existed.
  const idx = schedule.startStop ? stops.findIndex((s) => s.id === schedule.startStop) : -1;
  const startMin = idx > 0 ? schedule.startMin - arrive[idx] / 60 : schedule.startMin;
  // ⚠️ The phantom pass is MODELLED, never SHOWN. `firstSec` is how far into
  // cycle 0 the bus is really at `startStop` — the first instant of service
  // — and `endSec` is where `end` falls, both as seconds after cycle 0's
  // start. Every ETA function keeps to [firstSec, endSec]: before 07:30 the
  // stops ahead of SD Lot on cycle 0 are not listed (the bus will first reach
  // them on cycle 1), and after 17:30 nothing is. `serviceMin` keeps the
  // authored start for anything that wants to print the hours.
  const firstSec = idx > 0 ? arrive[idx] : 0;
  const endSec = schedule.endMin == null ? null : (schedule.endMin - startMin) * 60;
  return {
    ...schedule, startMin, serviceMin: schedule.startMin, cycleSec: schedule.everySec,
    driveSec, speed, arrive, firstSec, endSec,
  };
}

// Wall-clock seconds after local midnight, in the timetable's zone. Intl, not
// the device's zone: a phone set to Toronto must not move the bus an hour.
const partsFmt = new Map();
function wallSeconds(t, tz) {
  if (!partsFmt.has(tz)) {
    partsFmt.set(tz, new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, hourCycle: 'h23', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }));
  }
  const get = {};
  for (const p of partsFmt.get(tz).formatToParts(new Date(t))) get[p.type] = Number(p.value);
  return (get.hour % 24) * 3600 + get.minute * 60 + get.second + (t % 1000) / 1000;
}

/** Epoch ms of the timetable's first cycle on the local day containing `t`. */
export function serviceStart(tt, t) {
  return t - (wallSeconds(t, tt.tz) - tt.startMin * 60) * 1000;
}

/**
 * Epoch ms of local midnight on the day containing `t`. What "today" means for
 * a sighting — NOT `serviceStart`, which is the timetable's first cycle and is
 * hours after the bus is really out: the tracker has it on the road from 08:45
 * on a timetable that starts at 11:00, and a 10:30 sighting is today's.
 */
export function localDayStart(t, tz = SHUTTLE_TZ) {
  return t - wallSeconds(t, tz) * 1000;
}

// The calendar date of an instant, in a zone — numeric parts, never a weekday
// NAME, which would be the locale's word for it.
const dateFmt = new Map();
/**
 * The ISO weekday (1 = Monday … 7 = Sunday) of the local day containing `t`
 * in `tz`. The ZONE's day, never the device's or the server's: a server in
 * UTC reads 19:00 on a Winnipeg Friday as Saturday.
 */
export function localWeekday(t, tz = SHUTTLE_TZ) {
  if (!dateFmt.has(tz)) {
    dateFmt.set(tz, new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }));
  }
  const get = {};
  for (const p of dateFmt.get(tz).formatToParts(new Date(t))) get[p.type] = Number(p.value);
  const d = new Date(Date.UTC(get.year, get.month - 1, get.day)).getUTCDay();
  return d === 0 ? 7 : d;
}

/**
 * Does the timetable run on the local day containing `t`? A timetable built
 * without `days` (by hand, or before the field) runs on the default days.
 */
function runsOn(tt, t) {
  const days = Array.isArray(tt.days) ? tt.days : DEFAULT_SERVICE_DAYS;
  return days.includes(localWeekday(t, tt.tz));
}

/**
 * Epoch ms of the next first departure STRICTLY after `now` — today's if it
 * is still ahead, else the next service day's — or null with no timetable.
 * Skips the days the timetable does not run, so a Friday evening names
 * Monday 07:30.
 *
 * ⚠️ Each candidate day is found from a MIDDAY probe, never from `now`
 * itself. `serviceStart` reads the wall clock of the instant it is handed and
 * assumes that UTC offset held all day; asked at 01:00 on a daylight-saving
 * morning it would put 07:30 an hour out. Midday is always on the far side of
 * a 02:00 change, and so is 07:30 — and twelve hours of slack absorbs the
 * hour a week of probes 24 h apart drifts across a change.
 */
export function nextServiceStart(route, now) {
  const tt = route?.timetable;
  if (!tt || !finite(now)) return null;
  const H = 60 * 60 * 1000;
  const midnight = localDayStart(now, tt.tz);
  for (let d = 0; d <= 7; d++) {
    const probe = midnight + (12 + 24 * d) * H;
    if (!runsOn(tt, probe)) continue;
    const at = serviceStart(tt, probe) + tt.firstSec * 1000;
    if (at > now) return at;
  }
  return null;
}

/**
 * A millisecond, in seconds. The timetable has no sub-second precision, and
 * the re-anchored cycle start is a fraction of a minute — so "exactly 07:30"
 * comes out a few nanoseconds past the start stop's own offset and, compared
 * bare, rolled that stop over to the next cycle on the very instant the
 * service begins. Every comparison against the hours allows this much.
 */
const TT_EPS = 1e-3;

/**
 * Whether `offset` seconds after cycle 0's start is inside the day's service:
 * from the bus being at `startStop` at `start`, to `end` (open-ended without).
 */
function offsetInService(tt, offset) {
  return offset >= tt.firstSec - TT_EPS && (tt.endSec == null || offset <= tt.endSec + TT_EPS);
}

/**
 * Is the service running at `now`? False before the day's first departure,
 * after `end`, on a day it does not run (`days`; Saturday and Sunday by
 * default), and always without a timetable (a route with no timetable has
 * no hours to be outside of — its times come from sightings alone).
 *
 * ⚠️ This is the one gate every time function shares — `shuttleEtas`,
 * `timetableEtas`, `scheduledAt`, `scheduleDelay` — so that outside service
 * hours no phone, widget or server answer carries a stop time at all. The
 * tracker may still be on the road at 07:20 and the position is still served;
 * what is withheld is the promise of an arrival the service does not make.
 */
export function inService(route, now) {
  const tt = route?.timetable;
  if (!tt || !finite(now)) return false;
  return runsOn(tt, now) && offsetInService(tt, (now - serviceStart(tt, now)) / 1000);
}

/**
 * When, within a cycle, the timetable has the bus at position `at`:
 * `[from, to]` seconds after the cycle's start — a window while it waits at a
 * stop, a single instant anywhere on the road between two.
 */
export function scheduledWindow(route, at) {
  const tt = route.timetable;
  const n = route.stops.length;
  const i = legIndexAt(route, at);
  const into = forwardDistance(route.stops[i].at, at, route.length);
  const toNext = legLength(route, i) - into;
  if (into <= AT_STOP_M) return [tt.arrive[i], tt.arrive[i] + tt.dwellSec];
  if (toNext <= AT_STOP_M) {
    const a = i + 1 === n ? tt.cycleSec : tt.arrive[i + 1];
    return [a, a + tt.dwellSec];
  }
  const x = tt.arrive[i] + tt.dwellSec + into / tt.speed;
  return [x, x];
}

/**
 * How late the bus is: a position (`at` on the loop, seen at `t`) against the
 * timetable. Positive is late, negative early, 0 inside the window. The cycle
 * compared against is the NEAREST one — with a 15-minute cycle a bus 8 minutes
 * late is indistinguishable from one 7 minutes early, and the nearest reading
 * is the one right more often.
 *
 * Returns `{ delaySec, cycle, cycleStartT, scheduledT }`, or null with no
 * timetable or outside service hours — a tracker warming up on the road at
 * 07:20 is not "ten minutes early" for a departure that has not been made.
 */
export function scheduleDelay(route, pos) {
  const tt = route?.timetable;
  if (!tt || !pos || !finite(pos.at) || !finite(pos.t)) return null;
  if (!inService(route, pos.t)) return null;
  const origin = serviceStart(tt, pos.t);
  const [from, to] = scheduledWindow(route, pos.at);
  const elapsed = (pos.t - origin) / 1000;
  const k0 = Math.floor((elapsed - from) / tt.cycleSec);
  let best = null;
  for (const k of [k0 - 1, k0, k0 + 1]) {
    if (k < 0) continue;
    const a = k * tt.cycleSec + from;
    const b = k * tt.cycleSec + to;
    // Only a scheduled instant the service actually keeps: not the phantom
    // pass before the first departure, nothing after the day's end.
    if (!offsetInService(tt, a)) continue;
    const delay = elapsed < a ? elapsed - a : elapsed > b ? elapsed - b : 0;
    if (!best || Math.abs(delay) < Math.abs(best.delaySec)) {
      best = { delaySec: Math.round(delay), cycle: k,
        cycleStartT: origin + k * tt.cycleSec * 1000, scheduledT: origin + a * 1000 };
    }
  }
  return best;
}

/**
 * Where the timetable has the bus `offset` seconds into a cycle: metres along
 * the loop. The inverse of `scheduledWindow` — waiting at a stop for its
 * dwell, then driving at the one timetable speed to the next.
 */
export function positionAtOffset(route, offset) {
  const tt = route.timetable;
  const n = route.stops.length;
  const o = ((offset % tt.cycleSec) + tt.cycleSec) % tt.cycleSec;
  let i = n - 1;
  while (i > 0 && tt.arrive[i] > o) i--;
  const leaving = tt.arrive[i] + tt.dwellSec;
  if (o <= leaving) return route.stops[i].at; // waiting at stop i
  return (route.stops[i].at + Math.min((o - leaving) * tt.speed, legLength(route, i))) % route.length;
}

/**
 * Where the bus should be NOW, given where it was last seen: run forward from
 * the sighting, standing at each stop for its dwell and driving each leg at
 * the best pace there is for it — the MEASURED pace (`route.pace`) first,
 * then the middle of the leg's timed history, then the timetable. A sighting
 * at a stop is taken as the bus ARRIVING there, which is what "it's here"
 * means — unless `pos.moving` says otherwise: a tracker whose last two pings
 * show it rolling, a few metres past a stop, has left it, and waiting out a
 * dwell already behind it would park the marker while the bus drove away.
 *
 * This is a simulation, and the caller decides how long to trust it (a
 * sighting past its source's freshness window should stop the bus, not carry
 * it round campus). Null with no pace, no timetable and no leg history: there
 * is then nothing to run at, and the bus waits where it was seen.
 */
export function simulatedAt(route, pos, now, legStats = null) {
  if (!route || !pos || !finite(pos.at) || !finite(pos.t)) return null;
  const history = hasLegHistory(route, legStats);
  // Nothing to run at: the bus waits where it was seen (the caller's rule,
  // not a fallback here).
  if (!route.pace && !route.timetable && !history) return null;
  const elapsed = Math.max(0, (now - pos.t) / 1000);
  if (elapsed === 0) return pos.at;
  return runForwardAt(route, pos.at, elapsed, history ? legStats : null, pos.moving === true);
}

/** Does any leg carry enough timed runs for `legSeconds` to quote history? */
export function hasLegHistory(route, legStats) {
  if (!route || !legStats) return false;
  return route.stops.some((_, i) => {
    const s = legStats[legKey(route, i)];
    return !!s && s.n >= MIN_LEG_SAMPLES;
  });
}

/**
 * Walk `seconds` forward from `at0`: stand at each stop reached for its dwell,
 * then drive the leg piece by piece at the pace `walkLegs` gives each piece.
 *
 * With a `pace` block that is what the tracker has measured this bus doing,
 * stretch by stretch — 38 s standing at Dafoe, straight through Research
 * without stopping, a queue at the lights short of SD Lot 1 — and without one,
 * each leg is a single piece at the middle of its history, or the timetable's
 * figure, which makes the walk with no pace and no stats exactly the
 * `positionAtOffset` walk it generalises.
 *
 * A start within `AT_STOP_M` of a stop is the bus ARRIVING there (dwell
 * ahead of it), which is what a sighting at a stop means — unless it is
 * `moving`, when the dwell is behind it.
 */
function runForwardAt(route, at0, seconds, legStats, moving = false) {
  const n = route.stops.length;
  const legs = walkLegs(route, legStats);
  let i = legIndexAt(route, at0);
  let into = forwardDistance(route.stops[i].at, at0, route.length);
  let atStop = into <= AT_STOP_M && !moving;
  // The walk is periodic — one full loop takes the sum of its legs — so hours
  // of elapsed time reduce to a position inside one loop rather than a walk
  // that has to take every lap (with a timetable and no history that period is
  // exactly `cycleSec`, which keeps this the `positionAtOffset` walk it
  // generalises, however old the sighting).
  let loopSec = 0;
  for (const l of legs) loopSec += l.dwell + sum(l.pieces);
  let left = loopSec > 0 ? seconds % loopSec : seconds;
  for (let hops = 0; hops <= 2 * n; hops++) {
    const { dwell, pieces } = legs[i];
    if (atStop) {
      if (left <= dwell) return (route.stops[i].at + into) % route.length;
      left -= dwell;
    }
    // Equal-length pieces of THIS leg as the route now measures it, so a road
    // re-traced a few metres longer stretches the measured figures with it.
    const len = legLength(route, i);
    const w = len / pieces.length;
    for (let j = Math.min(pieces.length - 1, Math.floor(into / w)); j < pieces.length; j++) {
      const end = j === pieces.length - 1 ? len : (j + 1) * w;
      const span = Math.max(0, end - into);
      const sec = pieces[j] * (span / w);
      if (left < sec) return (route.stops[i].at + into + span * (left / sec)) % route.length;
      left -= sec;
      into = end;
    }
    i = (i + 1) % n;
    into = 0;
    atStop = true;
  }
  return route.stops[i % n].at;
}

const sum = xs => xs.reduce((a, b) => a + b, 0);

/**
 * Where the timetable alone has the bus at `now` — no sighting at all. What
 * the map draws (marked approximate) when nobody has tracked or reported the
 * bus today, so there is always SOME idea of where it is. Null outside
 * service: before the bus's first departure, or after `end`.
 */
export function scheduledAt(route, now) {
  const tt = route?.timetable;
  if (!tt || !finite(now) || !runsOn(tt, now)) return null;
  const elapsed = (now - serviceStart(tt, now)) / 1000;
  if (!offsetInService(tt, elapsed)) return null;
  return positionAtOffset(route, elapsed);
}

/** "3 min late" · "2 min early" · "On time" — rounded, and never "0 min late". */
export function delayText(delaySec) {
  if (!finite(delaySec) || Math.abs(delaySec) < ON_TIME_SEC) return 'On time';
  const m = Math.round(Math.abs(delaySec) / 60);
  return `${m} min ${delaySec > 0 ? 'late' : 'early'}`;
}

/**
 * The timetable alone: each stop's next scheduled arrival at or after `now`,
 * within today's service. What the sheet shows when nobody is tracking the
 * bus — a time the service promises is more use than a dash. Rows in the
 * order the times come, each `{ id, name, index, eta, seconds, basis:
 * 'schedule' }`.
 *
 * Nothing outside service hours — before the first departure (07:30 at SD
 * Lot) or after `end` — and a stop whose next arrival would fall after `end`
 * is left out: the service does not promise it. On the first cycle the stops
 * AHEAD of `startStop` are not listed either; their first time is on the next
 * cycle, which is the first time the bus really passes them.
 */
export function timetableEtas(route, now) {
  const tt = route?.timetable;
  if (!tt || !inService(route, now)) return [];
  const origin = serviceStart(tt, now);
  const elapsed = (now - origin) / 1000;
  const rows = [];
  route.stops.forEach((s, i) => {
    let k = Math.max(0, Math.ceil((elapsed - tt.arrive[i] - TT_EPS) / tt.cycleSec));
    let offset = k * tt.cycleSec + tt.arrive[i];
    if (offset < tt.firstSec - TT_EPS) { k += 1; offset = k * tt.cycleSec + tt.arrive[i]; }
    if (!offsetInService(tt, offset)) return;
    const eta = origin + offset * 1000;
    rows.push({ id: s.id, name: s.name, index: i, eta, seconds: Math.round((eta - now) / 1000), basis: 'schedule' });
  });
  return rows.sort((a, b) => a.eta - b.eta);
}

// ── The pace: where on the loop the bus actually spends its time ─────────────
//
// What a bus nobody has reported for a while is run forward on. Measured from
// every drive the tracker has recorded (2026-09-22 → 09-28, 13.5 loops),
// because neither older model survives contact with the real bus:
//
//  · The TIMETABLE drives one constant speed and stands 20 s at every stop.
//    The real bus stands 41 s at Dafoe and 45 s at SD Lot 2, drives straight
//    through Research and Innovation without stopping, and queues at the same
//    lights every loop; its leg figures are off by up to 75%, both ways. Its
//    PHASE is worse than useless: the bus drives its loops back to back, not
//    to the clock, so where in the cycle it is drifts 3 min over a morning and
//    sat 6–9 min apart on different days. A bus placed by the timetable alone
//    lands a median 1.7 km from the real one — farther than a guess anywhere
//    on the loop would (1.3 km).
//  · The leg history's LOWER QUARTILE (what `legSeconds` quotes, rightly, for
//    ETAs), summed over eleven legs, is a loop a minute quicker than the
//    fastest the bus has driven and two under its usual. Walked forward it
//    runs the marker ahead of the real bus by ~100 s after ten minutes and
//    ~270 s after thirty.
//
// Replayed against the recorded drives, leaving each day out of what the
// figures were measured from: a rider's report run forward thirty seconds
// lands a median 32 m from the real bus (timetable 91 m); a tracker gone quiet
// for two minutes 67 m (carrying on at its last measured speed: 326 m); and
// neither drifts — fifteen minutes on it is within 12 s of the bus either way,
// where the lower-quartile walk was 135–150 s ahead.
//
// Its SHAPE is the pace block's; its overall SPEED follows the live history
// (`paceScale`), so a snowed-in January slows the walk without the block being
// measured again.

/** A pace figure past these is not a pace. */
const PACE_MAX_DWELL_SEC = 600;
const PACE_MAX_PIECES = 400;
/** How far the leg history may stretch or shrink the pace block's loop. */
export const PACE_SCALE_MIN = 0.8;
export const PACE_SCALE_MAX = 1.25;

/**
 * Validate a `pace` block against the stops it is for. Returns `{ dwell, legs }`
 * indexed like the stops — `dwell[i]` seconds standing at stop i, `legs[i]`
 * seconds of MOVING over each equal-length piece of the leg from stop i to
 * i+1 (the dwell at either end not included) — each null where the block has
 * no usable figure. Null when no leg survives, which is a real state: the walk
 * then runs on history and the timetable, as it did before there was a pace.
 *
 * Legs are keyed by stop ids ("dysart>university-college"), the key their
 * history is filed under, and pieces are FRACTIONS of the leg rather than
 * metres, so re-tracing the road leaves the figures standing. Renaming a stop's
 * id orphans them, which is the rule the history follows too.
 */
export function normalizePace(raw, stops) {
  if (!raw || typeof raw !== 'object' || !Array.isArray(stops) || stops.length < 2) return null;
  const n = stops.length;
  const dwellIn = raw.dwell && typeof raw.dwell === 'object' ? raw.dwell : {};
  const legsIn = raw.legs && typeof raw.legs === 'object' ? raw.legs : {};
  const dwell = stops.map(s => {
    const v = dwellIn[s.id];
    return finite(v) && v >= 0 && v <= PACE_MAX_DWELL_SEC ? v : null;
  });
  const legs = stops.map((s, i) => {
    const xs = legsIn[`${s.id}>${stops[(i + 1) % n].id}`];
    if (!Array.isArray(xs) || !xs.length || xs.length > PACE_MAX_PIECES) return null;
    if (!xs.every(v => finite(v) && v >= 0 && v <= LEG_MAX_SEC)) return null;
    return sum(xs) > 0 ? xs.slice() : null;
  });
  return legs.some(Boolean) ? { dwell, legs } : null;
}

/**
 * Seconds leg i takes for the WALK: the middle of its history — not the lower
 * quartile `legSeconds` quotes for ETAs. That quartile is deliberate for a
 * time ("seem late, never early") and wrong for a position: summed over the
 * loop it is quicker than the bus has ever gone, and the error grows with
 * every minute the marker is run forward. Timetable, then distance, without
 * history.
 */
function walkLegSeconds(route, i, legStats) {
  const s = legStats?.[legKey(route, i)];
  if (s && s.n >= MIN_LEG_SAMPLES) {
    const sec = [s.median, s.sec].find(v => finite(v) && v > 0);
    if (sec != null) return sec;
  }
  return legSeconds(route, i, null).sec;
}

/**
 * How much slower (> 1) or quicker than its pace block the bus is running
 * lately: the history's loop over the block's, each summed over EVERY leg.
 *
 * Summed over the loop, never leg by leg, because the two file a stop's dwell
 * differently. History times a leg between the moments the bus crosses each
 * stop's authored point, and the bus stands wherever the kerb is, either side
 * of it: history files most of SD Lot 2's 45 s in the leg BEFORE the stop,
 * where the pace block has it at the stop. Leg by leg the two disagree by up
 * to 45 s; round the whole loop they agree to about 1%. 1 unless every leg has
 * both; clamped, so a history poisoned by something odd cannot stretch the
 * loop past reason.
 */
export function paceScale(route, legStats) {
  const pace = route?.pace;
  if (!pace || !legStats) return 1;
  let hist = 0;
  let own = 0;
  for (let i = 0; i < route.stops.length; i++) {
    const s = legStats[legKey(route, i)];
    if (!pace.legs[i] || !s || !(s.n >= MIN_LEG_SAMPLES)) return 1;
    const h = [s.median, s.sec].find(v => finite(v) && v > 0);
    if (h == null) return 1;
    hist += h;
    own += (pace.dwell[i] ?? dwellOf(route)) + sum(pace.legs[i]);
  }
  return own > 0 ? Math.min(PACE_SCALE_MAX, Math.max(PACE_SCALE_MIN, hist / own)) : 1;
}

/**
 * What the walk runs each leg at: `{ dwell, pieces }` — seconds standing at
 * the leg's first stop, then seconds of moving over each equal-length piece.
 * The pace block's where it has the leg (times `paceScale`); otherwise one
 * piece, sized by `walkLegSeconds` less the dwell. That dwell is capped at half
 * the leg so a short history figure cannot leave a negative drive.
 */
function walkLegs(route, legStats) {
  const pace = route.pace;
  const k = paceScale(route, legStats);
  return route.stops.map((_, i) => {
    const dwell = pace?.dwell[i] ?? dwellOf(route);
    if (pace?.legs[i]) return { dwell: dwell * k, pieces: pace.legs[i].map(s => s * k) };
    const sec = walkLegSeconds(route, i, legStats);
    const d = Math.min(dwell, sec / 2);
    return { dwell: d, pieces: [sec - d] };
  });
}

// ── Tracker pings → arrivals, legs, trips (server) ───────────────────────────

/**
 * Advance the trip state machine by one accepted tracker ping.
 *
 *   state: { at, t, lastStop, lastStopT, tripT, legsInTrip } | null
 *   ping:  { lat, lng, t }   (t = epoch ms)
 *
 * Returns `{ state, arrivals, legs, trips }`. A stop counts as ARRIVED when the
 * bus's position along the loop crosses it — not when a ping lands inside a
 * radius, because at 20-second pings a bus doing 30 km/h covers 170 m and
 * would drive straight past a stop between two of them. The crossing time is
 * interpolated between the two pings.
 *
 * A leg is arrival-to-arrival (so it includes the dwell at its first stop),
 * and it is only recorded when the two stops were consecutive with no gap in
 * the pings — a leg timed across a dropped signal is a guess, and the averages
 * are the one thing on the admin page somebody will act on.
 */
export function advanceTrip(route, state, ping) {
  const out = { state, arrivals: [], legs: [], trips: [] };
  const snap = projectOnRoute(route, ping, state?.at);
  if (!snap || snap.off > MAX_OFF_ROUTE_M) return out; // off the road: ignore, keep state

  const fresh = { at: snap.at, t: ping.t, lastStop: null, lastStopT: null, tripT: null, legsInTrip: 0 };
  if (!state || !finite(state.at) || !(ping.t > state.t) || ping.t - state.t > MAX_PING_GAP_MS) {
    // First ping, or a gap: we know where it is, not what it has been doing.
    return { ...out, state: fresh };
  }

  let d = forwardDistance(state.at, snap.at, route.length);
  if (route.length - d < JITTER_BACK_M) d = 0; // jitter behind: hasn't moved
  if (d > route.length / 2) return { ...out, state: fresh }; // jumped: re-seat

  const next = { ...state, at: d === 0 ? state.at : snap.at, t: ping.t };
  const n = route.stops.length;
  // Every stop whose position lies in (state.at, state.at + d], in order.
  const crossed = route.stops
    .map((s, i) => ({ i, rel: forwardDistance(state.at, s.at, route.length) }))
    .filter(c => c.rel > 0 && c.rel <= d)
    .sort((a, b) => a.rel - b.rel);

  for (const c of crossed) {
    const t = Math.round(state.t + (ping.t - state.t) * (c.rel / d));
    out.arrivals.push({ stop: route.stops[c.i].id, index: c.i, t });
    if (next.lastStop != null && (next.lastStop + 1) % n === c.i && finite(next.lastStopT)) {
      const sec = Math.round((t - next.lastStopT) / 1000);
      out.legs.push({ key: legKey(route, next.lastStop), from: route.stops[next.lastStop].id,
        to: route.stops[c.i].id, departT: next.lastStopT, arriveT: t, sec, outlier: sec > LEG_MAX_SEC });
      next.legsInTrip = (next.legsInTrip || 0) + 1;
    } else {
      next.legsInTrip = 0;
      next.tripT = null;
    }
    if (c.i === 0) {
      if (finite(next.tripT) && next.legsInTrip >= n) {
        out.trips.push({ startT: next.tripT, endT: t, sec: Math.round((t - next.tripT) / 1000) });
      }
      next.tripT = t;
      next.legsInTrip = 0;
    }
    next.lastStop = c.i;
    next.lastStopT = t;
  }
  return { ...out, state: next };
}

// ── Crowd reports (server) ───────────────────────────────────────────────────

/**
 * Where the crowd says the bus is. Reports are `{ lat, lng, t }` from students
 * who tapped "yes, it's here"; each is snapped to the road and anything off it
 * is ignored. Reports within `CROWD_CLUSTER_M` along the loop are one pass of
 * the bus, and the cluster with the most weight wins (a report's weight decays
 * with age) — so one student pressing Yes at the wrong stop is outvoted by two
 * pressing it at the right one, and a lone report still counts when it is all
 * there is. The position is the NEWEST report in the winning cluster.
 *
 * Returns `{ lat, lng, at, t, reports }` or null.
 */
export function crowdEstimate(route, reports, now) {
  if (!route || !Array.isArray(reports)) return null;
  const live = [];
  for (const r of reports) {
    if (!r || !finite(r.t) || now - r.t > CROWD_TTL_MS || r.t > now + 60_000) continue;
    const s = projectOnRoute(route, r);
    if (!s || s.off > CROWD_MAX_OFF_M) continue;
    live.push({ ...s, t: r.t, w: 1 - (now - r.t) / CROWD_TTL_MS });
  }
  if (!live.length) return null;
  let best = null;
  for (const a of live) {
    const members = live.filter(b => {
      const d = forwardDistance(a.at, b.at, route.length);
      return Math.min(d, route.length - d) <= CROWD_CLUSTER_M;
    });
    const weight = members.reduce((s, m) => s + m.w, 0);
    if (!best || weight > best.weight + 1e-9) best = { weight, members };
  }
  const newest = best.members.reduce((a, b) => (b.t > a.t ? b : a));
  return { lat: newest.lat, lng: newest.lng, at: newest.at, t: newest.t, reports: best.members.length };
}

/**
 * The one public answer: which position, if any, the apps should show.
 *
 *   tracker: { lat, lng, at, t } | null   (latest accepted ping)
 *   crowd:   crowdEstimate(...) | null
 *
 * 'tracker' shows the tracker only while it is fresh; 'crowd' shows the crowd
 * only; 'auto' prefers a fresh tracker and falls back to the crowd.
 */
export function pickShuttleSource(mode, tracker, crowd, now) {
  const trackerFresh = tracker && finite(tracker.t) && now - tracker.t <= TRACKER_FRESH_MS;
  const crowdFresh = crowd && finite(crowd.t) && now - crowd.t <= CROWD_TTL_MS;
  const m = SHUTTLE_MODES.includes(mode) ? mode : 'auto';
  // The replay is built by the caller, which is the only thing holding the
  // recording. Falling through to 'auto' here would put the REAL tracker back
  // on screen the moment somebody switched the demo on, which is the one
  // answer this mode must never give.
  if (m === 'simulate') return null;
  if (m === 'tracker') return trackerFresh ? { source: 'tracker', ...tracker } : null;
  if (m === 'crowd') return crowdFresh ? { source: 'crowd', ...crowd } : null;
  if (trackerFresh) return { source: 'tracker', ...tracker };
  return crowdFresh ? { source: 'crowd', ...crowd } : null;
}

// ── ETAs (phone) ─────────────────────────────────────────────────────────────

/** Seconds of dwell the model assumes at each stop. */
function dwellOf(route) {
  return route.timetable ? route.timetable.dwellSec : DEFAULT_DWELL_SEC;
}

/** The timetable's seconds for leg i: arrival i → arrival i+1, dwell at i included. */
function timetableLegSeconds(route, i) {
  const tt = route.timetable;
  const n = route.stops.length;
  const k = ((i % n) + n) % n;
  return (k === n - 1 ? tt.cycleSec : tt.arrive[k + 1]) - tt.arrive[k];
}

/**
 * The same leg history with every leg quoted at its MEDIAN — for times anchored
 * to a SIGHTING (a tracker ping or a rider's report, fresh or run forward).
 *
 * The lower quartile is the right caution for ONE leg and far too much for a
 * sum of them: five legs each at their fast quartile is a trip faster than the
 * bus has almost ever driven, so every row read early and every student waited
 * for it. Replayed against the recorded drives (2026-09-29, 3.6 h, 12 loops):
 * with a live tracker, times within ±2 min went from 63–69% to 91%, and the bus
 * reached a stop more than a minute EARLIER than shown 1–2% of the time — the
 * failure the quartile exists for, still rare.
 *
 * ⚠️ NOT for the no-sighting fallback (an invented departure on the current
 * cycle). The same replay had the median push a bus that was off its timetable
 * to 74% "earlier than shown", where the quartile held it at 54% — with nothing
 * observed, the cautious figure is the only protection there is. Callers pick:
 * sighting-anchored → this; fallback → the stats as served.
 */
export function typicalLegStats(legStats) {
  if (!legStats || typeof legStats !== 'object') return legStats;
  const out = {};
  for (const [k, s] of Object.entries(legStats)) {
    out[k] = s && finite(s.median) && s.median > 0 ? { ...s, p25: s.median } : s;
  }
  return out;
}

/**
 * Seconds leg i is expected to take: history when there is enough of it, the
 * timetable's figure when there is one, an assumed speed as the last resort.
 *
 * ⚠️ History quotes the LOWER QUARTILE, not the middle. The policy (the
 * contract's "seem late, never early"): a student told 6 minutes for a bus
 * that takes 4 walks out to a stop the bus has already left, and nothing on
 * their phone tells them so — where a student told 4 minutes for a bus that
 * takes 6 just waits two of them. Every estimate here errs toward the bus
 * being sooner and further along than the middle guess. `p25` is the newest
 * fold's; a cache from before the field existed falls back through the median
 * (better than the mean, which one lunch break drags) to the mean.
 *
 * Times anchored to a sighting are handed `typicalLegStats(legStats)`, which
 * quotes the median through this same field — see that function for why the
 * quartile is right for one leg and wrong for a sum of five.
 */
export function legSeconds(route, i, legStats) {
  const s = legStats?.[legKey(route, i)];
  if (s && s.n >= MIN_LEG_SAMPLES) {
    const sec = [s.p25, s.median, s.sec].find((v) => finite(v) && v > 0);
    if (sec != null) return { sec, basis: 'history' };
  }
  if (route.timetable) return { sec: timetableLegSeconds(route, i), basis: 'schedule' };
  return { sec: legLength(route, i) / DEFAULT_SPEED_MPS + DEFAULT_DWELL_SEC, basis: 'distance' };
}

/**
 * When the bus next reaches each stop.
 *
 *   pos:      { at, t }   where it was, and when (epoch ms)
 *   legStats: { [legKey]: { n, sec } }   from /api/shuttle
 *
 * Returns one row per stop, in the order the bus will reach them starting
 * with the next one: `{ id, name, index, eta (epoch ms), seconds, basis }`.
 * Time elapsed since the fix is subtracted, but never past the next stop:
 * a bus that is late is "due", not "departed 2 minutes ago".
 */
export function shuttleEtas(route, pos, legStats, now) {
  if (!route || !pos || !finite(pos.at) || !finite(pos.t)) return [];
  // A timetable's hours bound every promise, sighting or not: a tracker warming
  // up on the road at 07:20, or parked at 17:40, is a position and not a bus
  // that will reach anybody. Routes without a timetable have no hours.
  if (route.timetable && !inService(route, now)) return [];
  const n = route.stops.length;
  const i = legIndexAt(route, pos.at);
  const into = forwardDistance(route.stops[i].at, pos.at, route.length);
  const first = legSeconds(route, i, legStats);
  // The dwell at the first leg's own stop is behind a bus already out on the
  // road, so only the DRIVING share of that leg remains. This is exactly what
  // the timetable arithmetic this walk replaced did (leaving time, not
  // arrival), and dropping it would show every next stop a dwell late — the
  // wrong side to err on. A bus still AT the stop reads as about to leave,
  // which errs a dwell early: the right side. Floored at half the leg so a
  // history figure shorter than the assumed dwell cannot go negative.
  const drive = Math.max(first.sec - dwellOf(route), first.sec / 2);
  const len = legLength(route, i);
  const frac = Math.max(0, Math.min(1, 1 - into / len));
  const elapsed = Math.max(0, (now - pos.t) / 1000);
  // Within AT_STOP_M of either end the bus is AT the stop, not a few metres
  // shy of it: GPS noise around a stop must not shave seconds off the next
  // time, and a bus a bus-length short of a stop has arrived at it.
  let sec = len - into <= AT_STOP_M ? 0
    : Math.max(0, (into <= AT_STOP_M ? drive : frac * drive) - elapsed);
  const rows = [];
  // A row's basis is the weakest figure in its sum: one guessed leg makes the
  // total a guess, and one timetable leg makes a history total a promise.
  const rank = { history: 0, schedule: 1, distance: 2 };
  let basis = first.basis;
  // No listed time past the end of service, from a sighting any more than
  // from the timetable: a bus seen at 17:25 is not promised at U Lot at 17:38.
  const tt = route.timetable;
  const endT = tt && tt.endSec != null ? serviceStart(tt, now) + (tt.endSec + TT_EPS) * 1000 : Infinity;
  for (let k = 1; k <= n; k++) {
    const idx = (i + k) % n;
    const s = route.stops[idx];
    const eta = now + sec * 1000;
    if (eta <= endT) rows.push({ id: s.id, name: s.name, index: idx, seconds: Math.round(sec), eta, basis });
    const nx = legSeconds(route, idx, legStats);
    sec += nx.sec;
    if (rank[nx.basis] > rank[basis]) basis = nx.basis;
  }
  return rows;
}

/**
 * Fold leg records into `{ [key]: { n, sec, median, p25 } }`, outliers excluded.
 *
 * `p25` is the lower quartile (nearest rank): the leg time a quick run beats.
 * It is what the ETAs quote — see `legSeconds` for why the fast end and not
 * the middle — and it rides beside the mean and median rather than replacing
 * them, because a fold already cached before this field existed must still be
 * readable (`legSeconds` falls back through median to the mean).
 */
export function legStatsFrom(legs) {
  const by = new Map();
  for (const l of legs || []) {
    if (!l || l.outlier || !finite(l.sec) || l.sec <= 0 || typeof l.key !== 'string') continue;
    if (!by.has(l.key)) by.set(l.key, []);
    by.get(l.key).push(l.sec);
  }
  const out = {};
  for (const [key, xs] of by) {
    xs.sort((a, b) => a - b);
    const mid = xs.length >> 1;
    const median = xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
    const p25 = xs[Math.floor((xs.length - 1) * 0.25)];
    out[key] = { n: xs.length, sec: Math.round(xs.reduce((a, b) => a + b, 0) / xs.length), median, p25 };
  }
  return out;
}
